import { Box, VBox } from "@/components-v2/Box";
import { BackButton, CloseButton, IconButton } from "@/components-v2/Button";
import { ColumnSelectorButton, ColumnSelectorDialog } from "@/components-v2/ColumnSelectorDialog";
import { DataTable } from "@/components-v2/DataTable";
import { DateInput } from "@/components-v2/DateInput";
import { DropdownMenu } from "@/components-v2/Dropdown";
import { prepDateInput } from "@/components-v2/dateInputDomain";
import { FilterButton, FilterDialog } from "@/components-v2/FilterDialog";
import { FullScreenLayout } from "@/components-v2/FullScreenLayout";
import { ResizableSidepanelLayout } from "@/components-v2/ResizableSidepanelLayout";
import { SelectWithNavigator } from "@/components-v2/Select";
import { Tabs } from "@/components-v2/Tabs";
import { TextInput } from "@/components-v2/TextInput";
import {
  CompanyFieldEditDialog,
  CompanyFieldPanelView,
} from "@/companyField/companyFieldComponents";
import { CompanyFieldDetailsState } from "@/companyField/companyFieldDomain";
import { DCEvent, navigateTo } from "@/events/eventsDomain";
import AlertMessageStore from "@/stores/AlertMessageStore";
import { useDispatch, useI18n, useTranslation } from "@/stores/RootStoreContext";
import { preventDefault } from "@/utils/events";
import { when } from "@/utils/functions";
import { observer } from "mobx-react";
import { FC } from "react";
import { ReportRow } from "../search/portfolioV2ApiClient";
import {
  backToReportsRow,
  closeDetailsPanel,
  closeReportsColumnSelector,
  closeReportsFieldPanel,
  closeReportsFilterDialog,
  closeReportsImportResult,
  PortfolioReportsState,
  prepReportsColumnSelectorButton,
  prepReportsExcelMenu,
  prepReportsFilterButton,
  prepReportsMenu,
  prepReportsRowTable,
  prepReportsTable,
  reportFieldLabels,
  reportsAsOf,
  reportsColumnLabel,
  reportsCompanyLabel,
  reportsCompanyStep,
  ReportsNavigation,
  reportsFieldNavigation,
  reportsFieldSaved,
  reportsFieldStep,
  reportsRowNavigation,
  reportsSelectedRow,
  selectReportsCompany,
  selectReportsField,
  setDate,
  setDateAndSubmit,
  setReportsDetailsFilter,
  submitReportsColumnSelector,
  submitReportSearch,
  submitReportsFilterDialog,
  updateQuery,
  updateReportsColumnSelector,
  updateReportsFilterDialog,
} from "./reportsDomain";
import noop from "lodash/noop";
import { LoadingOverlay } from "@/components-v2/LoadingOverlay";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import { ImportResultDialog } from "@/customFields/customFieldsComponents";

const ReportsNavigationBar: FC<{ navigation: ReportsNavigation; onClose: DCEvent[] }> = observer(
  ({ navigation, onClose }) => {
    const labels = reportFieldLabels(navigation.result.fields);
    const previousCompany = reportsCompanyStep(navigation, -1);
    const nextCompany = reportsCompanyStep(navigation, 1);
    const previousField = reportsFieldStep(navigation, -1);
    const nextField = reportsFieldStep(navigation, 1);
    return (
      <VBox paddingTop paddingX gap={"sm"}>
        <Box alignItems={"center"} justifyContent={"space-between"} gap={"lg"}>
          <Box alignItems={"center"} gap={"lg"}>
            {when(backToReportsRow(navigation), (onBack) => (
              <BackButton variant={"secondary"} onClick={onBack} />
            ))}
            <SelectWithNavigator
              label={{ key: "company" }}
              labelSrOnly
              items={navigation.rows}
              value={navigation.row}
              idFn={(row) => row.id}
              labelFn={reportsCompanyLabel}
              onSelect={(row) => selectReportsCompany(navigation, row)}
              selectNext={nextCompany}
              selectNextTooltip={{ key: "portfolio.reports.next-company" }}
              selectPrev={previousCompany}
              selectPrevTooltip={{ key: "portfolio.reports.previous-company" }}
              arrows={"up-down"}
            />
          </Box>
          <Box alignItems={"center"} gap={"lg"}>
            {when(navigation.fieldKey, (fieldKey) => (
              <SelectWithNavigator
                label={{ key: "entity-data.field" }}
                labelSrOnly
                items={navigation.fieldKeys}
                value={fieldKey}
                idFn={(key) => key}
                labelFn={(key) => labels[key]}
                onSelect={(key) => selectReportsField(navigation, key)}
                selectNext={nextField}
                selectNextTooltip={{ key: "portfolio.reports.next-field" }}
                selectPrev={previousField}
                selectPrevTooltip={{ key: "portfolio.reports.previous-field" }}
              />
            ))}
            <CloseButton onClick={onClose} />
          </Box>
        </Box>
      </VBox>
    );
  },
);

const ReportsRowPanel = observer(
  ({ state, row }: { state: PortfolioReportsState; row: ReportRow }) => {
    const i18n = useI18n();
    return (
      <VBox grow overflow={"hidden"} gap={false}>
        {when(reportsRowNavigation(state, row, i18n), (navigation) => (
          <ReportsNavigationBar navigation={navigation} onClose={[closeDetailsPanel]} />
        ))}
        <VBox borderBottom paddingTop={"lg"} paddingX={"md"} gap={"md"}>
          <VBox>
            <h4>{i18n.tf(reportsCompanyLabel(row))}</h4>
            {when(reportsAsOf(state), (asOf) => (
              <Box fg={"subtle"} textSize={"sm"}>
                {i18n.tf(asOf)}
              </Box>
            ))}
          </VBox>
          <Tabs
            tabs={[
              {
                id: "entity-data",
                label: { key: "entity-data.title" },
                isActive: true,
                onSelect: [],
              },
            ]}
          />
        </VBox>
        <VBox grow overflow={"auto"} padding>
          <Box alignItems={"center"} justifyContent={"space-between"} gap={"md"} paddingY={"sm"}>
            <TextInput
              type={"search"}
              placeholder={{ key: "search" }}
              value={state.detailsFilter ?? ""}
              onChange={(event) => setReportsDetailsFilter(event.target.value)}
              inline
            />
            {state.importingOrgNo === row.orgNo ? (
              <LoadingIndicator />
            ) : (
              when(prepReportsExcelMenu(state, row), (menu) => <DropdownMenu {...menu} />)
            )}
          </Box>
          {when(prepReportsRowTable(state, row), (props) => (
            <DataTable stickyHeader {...props} />
          ))}
        </VBox>
      </VBox>
    );
  },
);

const ReportsFieldPanel: FC<{
  state: PortfolioReportsState;
  fieldDetails: CompanyFieldDetailsState;
}> = observer(({ state, fieldDetails }) => {
  const i18n = useI18n();
  return (
    <VBox grow overflow={"hidden"} gap={false}>
      {when(reportsFieldNavigation(state, fieldDetails, i18n), (navigation) => (
        <ReportsNavigationBar navigation={navigation} onClose={closeReportsFieldPanel} />
      ))}
      <CompanyFieldPanelView state={fieldDetails} hideCloseButton />
    </VBox>
  );
});

const ReportsResultPanel: FC<{
  state: PortfolioReportsState;
  fieldDetails: CompanyFieldDetailsState | undefined;
}> = observer(({ state, fieldDetails }) => {
  const dispatch = useDispatch();

  return (
    <VBox grow overflow={"hidden"} gap={false}>
      <Box
        padding
        element={"form"}
        alignItems={"center"}
        htmlProps={{
          onSubmit: preventDefault(() => dispatch(submitReportSearch(state))),
        }}
      >
        <input type="submit" className="hidden" />
        <TextInput
          name={"query"}
          value={state.query ?? ""}
          onChange={(event) => updateQuery(event.target.value)}
          icon={"magnifying-glass"}
          placeholder={{ key: "portfolio.reports.query.placeholder" }}
          inline
        />
        <DateInput
          {...prepDateInput({
            state: state.date,
            onInput: setDate,
            onChange: setDateAndSubmit,
          })}
          placeholder={{ key: "portfolio.reports.date.placeholder" }}
          inline
          messageVariant={"tooltip"}
        />
        <ColumnSelectorButton {...prepReportsColumnSelectorButton(state)} />
        <FilterButton {...prepReportsFilterButton(state)} />
      </Box>
      <VBox grow overflow={"hidden"}>
        {when(prepReportsTable(state, fieldDetails), (props) => (
          <DataTable className="mx-md" variant={"IN_CARD"} stickyHeader {...props} />
        ))}
        {state.isLoading && <LoadingOverlay />}
      </VBox>
      {when(state.columnSelector, (columnSelector) => (
        <ColumnSelectorDialog
          state={columnSelector}
          getColumnLabel={(name) => reportsColumnLabel(state, name)}
          update={updateReportsColumnSelector}
          onSubmit={submitReportsColumnSelector}
          onCancel={closeReportsColumnSelector}
        />
      ))}
      {when(state.filterDialog, (filterDialog) => (
        <FilterDialog
          state={filterDialog}
          update={updateReportsFilterDialog}
          onSubmit={submitReportsFilterDialog}
          onCancel={closeReportsFilterDialog}
        />
      ))}
      <CompanyFieldEditDialog onSaved={reportsFieldSaved} />
      {when(state.importResult, (result) => (
        <ImportResultDialog result={result} onClose={() => dispatch(closeReportsImportResult)} />
      ))}
    </VBox>
  );
});

const ReportTitle = observer(() => {
  const { t } = useTranslation();
  return (
    <Box alignItems={"center"}>
      <span>{t("portfolio.reports.new-report-title")}</span>
      <IconButton icon={"edit"} disabled onClick={noop} tooltip={{ key: "edit" }} />
    </Box>
  );
});

type ReportsViewProps = {
  state: PortfolioReportsState | undefined;
  fieldDetails: CompanyFieldDetailsState | undefined;
  alertStore: AlertMessageStore;
};

export const ReportsView: FC<ReportsViewProps> = observer(({ state, fieldDetails, alertStore }) => {
  return (
    <FullScreenLayout
      bg
      appBar={false}
      alertStore={alertStore}
      pageHeader={
        state && {
          onGoBack: [navigateTo({ name: "portfolio/reports" })],
          onGoBackVariant: "x",
          title: <ReportTitle />,
          menu: prepReportsMenu(state),
        }
      }
      centerPanel={
        !state ? (
          "loader"
        ) : (
          <ResizableSidepanelLayout
            containerProps={{ overflow: "hidden", grow: true }}
            centerPanel={<ReportsResultPanel state={state} fieldDetails={fieldDetails} />}
            rightBaseSizePercent={50}
            rightPanel={
              fieldDetails ? (
                <ReportsFieldPanel state={state} fieldDetails={fieldDetails} />
              ) : (
                when(reportsSelectedRow(state), (row) => (
                  <ReportsRowPanel state={state} row={row} />
                ))
              )
            }
          />
        )
      }
    />
  );
});
